/**
 * /settings/organization — Organization section wrapper (#851).
 *
 * Header + context bar (`Organization · <name>`) + pill sub-nav +
 * `<Outlet context={...} />`. Threads the active org + identity
 * refresh callback down via Outlet context.
 *
 * Visibility tightening from #851 Option A: tabs that were "show
 * to Member as read-only" in the old shell now drop `'member'`
 * from their gate. Member without a workspace role lands on
 * `/settings` and sees only the Account card; the Organization
 * card hides because no tab gates them in. Removed Member access:
 *
 *   - General      (was ['owner','admin','billing','member'] — Member saw a disabled form)
 *   - Members      (was ['owner','admin','billing','member'] — Member saw read-only roster)
 *   - Teams        (was ['owner','admin','billing','member'] — Member saw read-only list)
 *   - Workspaces   (was ['owner','admin','member']           — Member saw read-only list)
 *
 * Each of those tabs' mutation gates already reject Member at the
 * `useCanI` layer; the new tab-visibility gate matches the mutation
 * gate so "Settings" stays a "things you can SET" surface.
 */
import { useMemo } from 'react'
import { Navigate, Outlet, useOutletContext } from 'react-router-dom'
import type { OrgWithRole, WorkspaceSummary } from '@/lib/api'
import { useAppContext } from '@/hooks/useAppContext'
import { useIdentity } from '@/hooks/useIdentity'
import { useOrgEntitlements } from '@/hooks/useOrgEntitlements'
import { SettingsSubNav, type SettingsTab } from '../SettingsSubNav'

type OrgRole = OrgWithRole['role']

interface OrgTabDef extends SettingsTab {
  /** Org roles that may see this tab. The first, always-present gate. */
  gate: readonly OrgRole[]
  /** Optional plan-feature gate layered ON TOP of the role gate
   * (#1106). When set, the tab is visible only if the org's plan
   * entitles `requiresFeature` AND the role gate passes. The string
   * is a backend `FeatureKey`; kept as a plain string here so the
   * frontend doesn't import the server's feature-catalog type. Future
   * gated org tabs add their key the same way — the visibility filter
   * already knows how to honour it. */
  requiresFeature?: string
  /** Optional billing gate layered ON TOP of the role gate. When set,
   * the tab is visible only when this deployment bills its customers,
   * a sign-in handoff URL is configured, and the user is in exactly one
   * organization (see {@link OrgBillingSignal}). Its `href` is filled
   * from the handoff URL at filter time, so it navigates out of the SPA
   * to the billing page rather than to an in-app route. */
  requiresBilling?: boolean
}

/** The billing signal that gates the Billing tab, read from the boot
 * identity payload. Kept as a small struct rather than three positional
 * args so both call sites (the wrapper and the Settings landing) pass the
 * same shape. */
export interface OrgBillingSignal {
  /** This deployment bills its customers at all (the server's `gateApplies`
   * predicate, surfaced on `/me`). */
  enabled: boolean
  /** Where the Billing entry links: the sign-in handoff start URL, or
   * null/absent when this install hands nobody over. */
  handoffUrl?: string | null
  /** The user belongs to exactly one organization. The handoff names a
   * billing organization only then (`customClaimsForOrgs`), so the entry
   * hides for a multi-organization user until that later slice lands. */
  singleOrg: boolean
}

const ORG_TABS: readonly OrgTabDef[] = [
  { to: 'general',      label: 'General',     gate: ['owner', 'admin'] },
  { to: 'members',      label: 'Members',     gate: ['owner', 'admin'] },
  { to: 'teams',        label: 'Teams',       gate: ['owner', 'admin'] },
  { to: 'workspaces',   label: 'Workspaces',  gate: ['owner', 'admin'] },
  // Run Budgets is gated behind the `runBudgets.configure` plan-feature
  // (#1106) — off for every plan at launch, so it's hidden from all
  // customers. Treatment is a full hide (no LockedCapability upsell):
  // an unfinished internal surface shouldn't be teased as a sellable
  // feature. The team re-reveals it on a specific org via the
  // resolver's `entitlementOverride.features.forceOn`.
  // #1792 — hidden unless the org's plan includes single sign-on, the
  // same full-hide treatment Run budgets gets. A customer without the
  // feature sees no trace of it rather than a locked teaser.
  { to: 'single-sign-on', label: 'Single sign-on', gate: ['owner', 'admin'], requiresFeature: 'sso' },
  { to: 'run-budgets',  label: 'Run budgets', gate: ['owner', 'admin'], requiresFeature: 'runBudgets.configure' },
  { to: 'usage',        label: 'Usage',       gate: ['owner', 'admin', 'billing'] },
  // Billing is not an in-app route: it links out to the billing page served
  // on the same site through the sign-in handoff. Shown only where this
  // deployment bills its customers and the user is in one organization; see
  // the requiresBilling gate in getVisibleOrganizationTabs.
  { to: 'billing',      label: 'Billing',     gate: ['owner', 'admin', 'billing'], requiresBilling: true },
  { to: 'audit',        label: 'Audit log',   gate: ['owner', 'admin'] },
  { to: 'recovery',     label: 'Recovery',    gate: ['owner', 'admin'] },
  { to: 'danger',       label: 'Danger',      gate: ['owner'], danger: true },
]

export interface OrganizationOutletContext {
  org: OrgWithRole
  workspace: WorkspaceSummary
  /** Tab actions that mutate org-level state call this so the
   * identity-derived org name / role updates next render. Same
   * shape as the legacy Settings shell's `refreshIdentity`
   * threading — tab components like `OrgGeneral` and `OrgDanger`
   * rely on it. */
  refreshIdentity: () => Promise<void>
}

/** Tab-list filter for a given org role + plan entitlements. Pure;
 * used by both the wrapper (to render the sub-nav) and the Settings
 * landing page (to decide whether the Organization card shows). Org
 * Member with no other role gets zero tabs → card hides (#851
 * Option A).
 *
 * Two-layer gate (#1106): a tab shows only when BOTH the role gate
 * passes AND, if the tab declares `requiresFeature`, the org's plan
 * entitles that feature. The `hasFeature` predicate is threaded in
 * from `useOrgEntitlements` so this function stays pure (the async
 * fetch lives in the hook). Safe-default: when `hasFeature` is
 * omitted (entitlements not yet loaded, or a caller that has no
 * entitlement context) a feature-gated tab is treated as NOT entitled
 * → hidden. This matches the `useOrgEntitlements` hook's own
 * locked-until-confirmed default — a transient fetch failure can never
 * flash a gated tab into view. Role-only tabs are unaffected by the
 * predicate's absence. */
export function getVisibleOrganizationTabs(
  role: OrgRole | undefined,
  hasFeature?: (key: string) => boolean,
  billing?: OrgBillingSignal,
): readonly SettingsTab[] {
  if (!role) return []
  return ORG_TABS.filter((t) => {
    if (!t.gate.includes(role)) return false
    if (t.requiresFeature) {
      // Hidden until the entitlement read confirms the feature is on.
      return hasFeature ? hasFeature(t.requiresFeature) : false
    }
    if (t.requiresBilling) {
      // Shown only where there is billing to reach AND the handoff can name
      // this user's billing organization. Absent signal (a self-hosted
      // engine, or an older server) reads as off, so the entry stays hidden
      // rather than appearing with nowhere to go.
      return Boolean(billing?.enabled && billing.handoffUrl && billing.singleOrg)
    }
    return true
  }).map((t) =>
    // The billing entry links out of the SPA, so give it an `href` from the
    // handoff URL instead of an in-app route. Guaranteed present here by the
    // filter above.
    t.requiresBilling ? { to: t.to, label: t.label, href: billing?.handoffUrl ?? undefined } : t,
  )
}

export function OrganizationWrapper() {
  const { workspace } = useAppContext()
  const { state, refreshIdentity } = useIdentity()

  const org = useMemo<OrgWithRole | undefined>(() => {
    if (state.status !== 'ready' || !workspace?.orgId) return undefined
    return state.identity.orgs.find((o) => o._id === workspace.orgId)
  }, [state, workspace?.orgId])

  // Plan entitlements drive the feature-gated tab (#1106 — Run
  // Budgets). `hasFeature` returns false while loading / on error, so
  // a gated tab stays hidden until the read confirms it; no flash.
  const { hasFeature } = useOrgEntitlements(org?._id)

  // The billing signal gates the Billing entry. Read from the boot identity:
  // whether this deployment bills, where the handoff goes, and whether the
  // user is in exactly one organization (the handoff names a billing org only
  // then). Undefined until identity is ready, which reads as off.
  const billing = useMemo<OrgBillingSignal | undefined>(() => {
    if (state.status !== 'ready') return undefined
    return {
      enabled: state.identity.billingEnabled ?? false,
      handoffUrl: state.identity.billingHandoffUrl,
      singleOrg: state.identity.orgs.length === 1,
    }
  }, [state])

  const visibleTabs = useMemo(
    () => getVisibleOrganizationTabs(org?.role, hasFeature, billing),
    [org?.role, hasFeature, billing],
  )

  if (!workspace || !org || state.status !== 'ready') return null

  // Same defensive redirect as the Workspace wrapper — a Member
  // direct-navigating here lands on the cards index, which hides
  // the Organization card for them. Prevents a 404-feeling state.
  if (visibleTabs.length === 0) {
    return <Navigate to="/settings" replace />
  }

  const ctx: OrganizationOutletContext = { org, workspace, refreshIdentity }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">
          Organization <span className="text-muted-foreground font-normal">· {org.name}</span>
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Org-wide configuration affecting every workspace and member under {org.name}.
        </p>
      </div>
      <SettingsSubNav tabs={visibleTabs} ariaLabel="Organization settings sub-navigation" />
      <div className="pt-2">
        <Outlet context={ctx} />
      </div>
    </div>
  )
}

/** Thin hook for tab components — extracts the org / workspace /
 * refreshIdentity from Outlet context. Mirrors the Workspace
 * wrapper's shape; per-section so tab components don't need to
 * repeat the generic. */
export function useOrganizationOutletContext(): OrganizationOutletContext {
  return useOutletContext<OrganizationOutletContext>()
}

// ── Tab route adapters ────────────────────────────────────────────────
// One per Organization tab. Each bridges Outlet context to the
// existing tab component's prop interface; App.tsx just maps the
// child route path to one of these. The Run-budgets adapter is new
// to this PR — `OrgRunBudgets` existed on disk but was unwired
// before #851.

import { OrgGeneral } from '@/pages/OrgSettings/General'
import { OrgMembers } from '@/pages/OrgSettings/Members'
import { OrgTeams } from '@/pages/OrgSettings/Teams'
import { OrgWorkspaces } from '@/pages/OrgSettings/Workspaces'
import { OrgRunBudgets } from '@/pages/OrgSettings/RunBudgets'
import { OrgSingleSignOn } from '@/pages/OrgSettings/SingleSignOn'
import { OrgUsage } from '@/pages/OrgSettings/Usage'
import { OrgAudit } from '@/pages/OrgSettings/Audit'
import { OrgRecovery } from '@/pages/OrgSettings/Recovery'
import { OrgDanger } from '@/pages/OrgSettings/Danger'

export function OrgGeneralRoute() {
  const { org, refreshIdentity } = useOrganizationOutletContext()
  return <OrgGeneral org={org} onUpdated={refreshIdentity} />
}
export function OrgMembersRoute() {
  const { org } = useOrganizationOutletContext()
  return <OrgMembers org={org} />
}
export function OrgTeamsRoute() {
  const { org } = useOrganizationOutletContext()
  return <OrgTeams org={org} />
}
export function OrgWorkspacesRoute() {
  const { org } = useOrganizationOutletContext()
  return <OrgWorkspaces org={org} />
}
export function OrgSingleSignOnRoute() {
  const { org } = useOrganizationOutletContext()
  return <OrgSingleSignOn org={org} />
}
export function OrgRunBudgetsRoute() {
  const { org } = useOrganizationOutletContext()
  return <OrgRunBudgets org={org} />
}
export function OrgUsageRoute() {
  const { org } = useOrganizationOutletContext()
  return <OrgUsage org={org} />
}
export function OrgAuditRoute() {
  const { org } = useOrganizationOutletContext()
  return <OrgAudit org={org} />
}
export function OrgRecoveryRoute() {
  const { org } = useOrganizationOutletContext()
  return <OrgRecovery org={org} />
}
export function OrgDangerRoute() {
  const { org, refreshIdentity } = useOrganizationOutletContext()
  return <OrgDanger org={org} onDeleted={refreshIdentity} />
}
